// src/services/chatbotFlujoService.js
// Chatbot profesional por flujo guiado (sin IA, 100% predecible)

import { obtenerUrlImagen } from '@/api/productos.js';

export const ESTADOS = {
  MENU_PRINCIPAL: 'MENU_PRINCIPAL',
  ORDEN_PEDIR_NUMERO: 'ORDEN_PEDIR_NUMERO',
  ORDEN_PEDIR_CEDULA: 'ORDEN_PEDIR_CEDULA',
  ORDEN_RESULTADO: 'ORDEN_RESULTADO',
  PRECIOS_MENU_CATEGORIAS: 'PRECIOS_MENU_CATEGORIAS',
  PRECIOS_LISTA_PRODUCTOS: 'PRECIOS_LISTA_PRODUCTOS',
  PRECIOS_DETALLE: 'PRECIOS_DETALLE',
  SERVICIOS_MENU: 'SERVICIOS_MENU',
  SERVICIOS_DETALLE: 'SERVICIOS_DETALLE',
  PAGOS_MENU: 'PAGOS_MENU',
  PAGOS_PEDIR_ORDEN: 'PAGOS_PEDIR_ORDEN',
  PAGOS_DATOS_BANCO: 'PAGOS_DATOS_BANCO',
  UBICACION: 'UBICACION',
  ASESOR_HUMANO: 'ASESOR_HUMANO',
  LIBRE: 'LIBRE'
};

export const normalizar = (t) =>
  (t || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

// ─────────────────────────────────────────────────────────────
// Helpers de validación de estado del equipo
// ─────────────────────────────────────────────────────────────

export const ordenPermitePago = (orden) => {
  if (!orden || !orden.nombreEstado) return false;
  const e = normalizar(orden.nombreEstado);
  return e.includes('reparado') || e.includes('listo') || e.includes('entregado');
};

export const ordenEnProceso = (orden) => {
  if (!orden || !orden.nombreEstado) return false;
  const e = normalizar(orden.nombreEstado);
  return (
    e.includes('recibido') ||
    e.includes('diagnostic') ||
    e.includes('reparacion') ||
    e.includes('repara')
  );
};

// ─────────────────────────────────────────────────────────────
// Formateadores de precios
// ─────────────────────────────────────────────────────────────
const formatUSD = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
const formatBs = (val) => new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 }).format(val) + ' Bs';

// ─────────────────────────────────────────────────────────────
// Menú principal
// ─────────────────────────────────────────────────────────────
export const construirMenuPrincipal = () => ({
  texto: '<strong>¿En qué te puedo ayudar?</strong>',
  opciones: [
    { id: 'opt_orden', label: '🔍 Consultar estado de reparación', accion: ESTADOS.ORDEN_PEDIR_NUMERO },
    { id: 'opt_precios', label: '💵 Precios de productos a la venta', accion: ESTADOS.PRECIOS_MENU_CATEGORIAS },
    { id: 'opt_servicios', label: '🛠️ Servicios técnicos disponibles', accion: ESTADOS.SERVICIOS_MENU },
    { id: 'opt_pagos', label: '💳 Registrar pago de mi orden', accion: ESTADOS.PAGOS_PEDIR_ORDEN },
    { id: 'opt_ubicacion', label: '📍 Ubicación y horario', accion: ESTADOS.UBICACION },
    { id: 'opt_asesor', label: '👤 Contactar asesor humano', accion: ESTADOS.ASESOR_HUMANO }
  ]
});

// ─────────────────────────────────────────────────────────────
// Servicios
// ─────────────────────────────────────────────────────────────
export const construirMenuServicios = () => ({
  texto:
    '🛠️ <strong>Servicios técnicos disponibles</strong><br><br>' +
    'Selecciona el servicio que te interesa:',
  opciones: [
    { id: 'srv_computacion', label: '💻 Computadoras y Laptops', accion: 'SERVICIO_COMPUTACION' },
    { id: 'srv_telefonos', label: '📱 Teléfonos inteligentes', accion: 'SERVICIO_TELEFONOS' },
    { id: 'srv_redes', label: '🌐 Redes e Internet', accion: 'SERVICIO_REDES' },
    { id: 'srv_seguridad', label: '📷 Cámaras y Alarmas', accion: 'SERVICIO_SEGURIDAD' },
    { id: 'srv_volver', label: '⬅️ Volver al menú', accion: ESTADOS.MENU_PRINCIPAL }
  ]
});

// ─────────────────────────────────────────────────────────────
// Pagos
// ─────────────────────────────────────────────────────────────
export const construirMenuPagosSinOrden = () => ({
  texto:
    '💳 <strong>Datos para Pago Móvil / Transferencia</strong><br><br>' +
    '• <strong>Banco de Venezuela (0102)</strong><br>&nbsp;&nbsp;CI: 18.731.281 | Tlf: 0412-5696219<br>' +
    '• <strong>Banco Mercantil (0105)</strong><br>&nbsp;&nbsp;CI: 18.731.281 | Tlf: 0412-5696219<br><br>' +
    '🕒 <em>Pagos disponibles de 8:30 AM a 5:30 PM.</em><br><br>' +
    '⚠️ Para registrar un pago primero debes consultar el estado de tu orden. ' +
    'El pago solo se habilita cuando el equipo esté <strong>reparado y listo para retirar</strong>.',
  opciones: [
    { id: 'pago_consultar_orden', label: '🔍 Consultar mi orden', accion: ESTADOS.ORDEN_PEDIR_NUMERO },
    { id: 'pago_volver', label: '⬅️ Volver al menú', accion: ESTADOS.MENU_PRINCIPAL }
  ]
});

export const construirMenuPagosConOrden = (orden) => {
  const puedePagar = ordenPermitePago(orden);
  const enProceso = ordenEnProceso(orden);

  const baseTexto =
    '💳 <strong>Datos para Pago Móvil / Transferencia</strong><br><br>' +
    '• <strong>Banco de Venezuela (0102)</strong><br>&nbsp;&nbsp;CI: 18.731.281 | Tlf: 0412-5696219<br>' +
    '• <strong>Banco Mercantil (0105)</strong><br>&nbsp;&nbsp;CI: 18.731.281 | Tlf: 0412-5696219<br><br>' +
    '🕒 <em>Pagos disponibles de 8:30 AM a 5:30 PM.</em><br><br>';

  if (puedePagar) {
    return {
      texto:
        baseTexto +
        `✅ Tu orden <strong>#${orden.numeroOrden || orden.idRecepcionEquipo}</strong> está en estado ` +
        `<strong>${(orden.nombreEstado || '').replace(/_/g, ' ').toUpperCase()}</strong>.<br>` +
        'Ya puedes registrar tu pago en el sistema.',
      opciones: [
        { id: 'pago_registrar', label: '📝 Registrar mi pago ahora', accion: 'ABRIR_MODAL_PAGO', orden },
        { id: 'pago_volver', label: '⬅️ Volver al menú', accion: ESTADOS.MENU_PRINCIPAL }
      ]
    };
  }

  if (enProceso) {
    return {
      texto:
        baseTexto +
        `⏳ Tu orden <strong>#${orden.numeroOrden || orden.idRecepcionEquipo}</strong> aún está en proceso ` +
        `(<strong>${(orden.nombreEstado || '').replace(/_/g, ' ').toUpperCase()}</strong>).<br><br>` +
        '🔒 <strong>El pago se habilita únicamente cuando el equipo esté reparado y listo para retirar.</strong><br>' +
        'Te notificaremos cuando esté listo.',
      opciones: [
        { id: 'pago_estado', label: '🔍 Ver estado de mi orden', accion: 'VER_ESTADO_ORDEN_ACTUAL' },
        { id: 'pago_wa', label: '💬 Consultar por WhatsApp', accion: 'ABRIR_WHATSAPP_ORDEN' },
        { id: 'pago_volver', label: '⬅️ Volver al menú', accion: ESTADOS.MENU_PRINCIPAL }
      ]
    };
  }

  return {
    texto:
      baseTexto +
      `ℹ️ Tu orden <strong>#${orden.numeroOrden || orden.idRecepcionEquipo}</strong> no tiene un estado de pago habilitado todavía.<br><br>` +
      'Por favor consulta con un asesor para verificar si ya puedes realizar el pago.',
    opciones: [
      { id: 'pago_estado', label: '🔍 Ver estado de mi orden', accion: 'VER_ESTADO_ORDEN_ACTUAL' },
      { id: 'pago_wa', label: '💬 Consultar por WhatsApp', accion: 'ABRIR_WHATSAPP_ORDEN' },
      { id: 'pago_volver', label: '⬅️ Volver al menú', accion: ESTADOS.MENU_PRINCIPAL }
    ]
  };
};

// ─────────────────────────────────────────────────────────────
// Ubicación y asesor
// ─────────────────────────────────────────────────────────────
export const construirUbicacion = () => ({
  texto:
    '📍 <strong>Jean Informático C.A.</strong><br><br>' +
    'Centro Comercial <strong>Latin Center</strong>, Planta Baja, locales 01 y 14.<br>' +
    'Calle 30 con Av. 33 — <strong>Acarigua, Portuguesa</strong>.<br><br>' +
    '🕒 <strong>Horario:</strong> Lunes a Sábado de 8:30 AM a 5:30 PM.',
  opciones: [
    { id: 'ub_mapa', label: '🗺️ Abrir en Google Maps', accion: 'ABRIR_MAPA', url: 'https://maps.google.com/?q=Jean+Informatico+Latin+Center+Acarigua' },
    { id: 'ub_volver', label: '⬅️ Volver al menú', accion: ESTADOS.MENU_PRINCIPAL }
  ]
});

export const construirAsesorHumano = () => ({
  texto:
    '👤 <strong>Atención personalizada</strong><br><br>' +
    'Nuestro equipo te atenderá por WhatsApp en horario laboral (8:30 AM – 5:30 PM, L–S).',
  opciones: [
    { id: 'asesor_wa', label: '💬 Abrir WhatsApp', accion: 'ABRIR_WHATSAPP', url: 'https://wa.me/584125696219?text=Hola%20Jean%20Inform%C3%A1tico%2C%20necesito%20asistencia%20personalizada' },
    { id: 'asesor_volver', label: '⬅️ Volver al menú', accion: ESTADOS.MENU_PRINCIPAL }
  ]
});

// ─────────────────────────────────────────────────────────────
// Procesamiento de texto libre
// ─────────────────────────────────────────────────────────────
export const procesarTextoLibre = (mensaje, estadoActual) => {
  const n = normalizar(mensaje);

  if (['menu', 'inicio', 'volver', 'cancelar', 'salir', 'atras'].some((k) => n === k)) {
    return { accion: ESTADOS.MENU_PRINCIPAL };
  }

  if (estadoActual === ESTADOS.ORDEN_PEDIR_NUMERO || estadoActual === ESTADOS.PAGOS_PEDIR_ORDEN) {
    const match = mensaje.match(/(?:ORD-\d{4}-\d+|#?\d{2,6})/i);
    if (match) {
      return {
        accion: 'BUSCAR_ORDEN',
        numeroOrden: match[0].replace('#', ''),
        contexto: estadoActual === ESTADOS.PAGOS_PEDIR_ORDEN ? 'pago' : 'consulta'
      };
    }
    return { texto: '❌ No reconocí un número de orden válido.<br>Ejemplos válidos: <em>ORD-2024-001</em> o <em>105</em>.<br>Escríbelo de nuevo o escribe <em>"menú"</em> para volver.' };
  }

  if (estadoActual === ESTADOS.ORDEN_PEDIR_CEDULA) {
    const cedula = mensaje.replace(/[^0-9]/g, '');
    if (cedula.length >= 6 && cedula.length <= 9) {
      return { accion: 'VALIDAR_CEDULA', cedula };
    }
    return { texto: '❌ La cédula debe tener entre 6 y 9 dígitos.<br>Escríbela de nuevo (ej: <em>18731281</em>) o escribe <em>"menú"</em> para volver.' };
  }

  if (estadoActual === ESTADOS.PRECIOS_MENU_CATEGORIAS || estadoActual === ESTADOS.PRECIOS_LISTA_PRODUCTOS) {
    return { accion: 'BUSCAR_PRODUCTO', termino: mensaje };
  }

  return {
    texto: 'No entendí tu mensaje. 🤔<br>Puedes elegir una de las opciones del menú o escribir <em>"menú"</em> para empezar de nuevo.',
    opciones: construirMenuPrincipal().opciones
  };
};

// ─────────────────────────────────────────────────────────────
// Formateadores de resultados
// ─────────────────────────────────────────────────────────────
export const formatearOrdenParaChat = (orden, totalUsd, totalBs) => {
  const fecha = orden.fechaIngreso
    ? new Date(orden.fechaIngreso).toLocaleDateString('es-VE', { year: 'numeric', month: 'short', day: 'numeric' })
    : 'N/A';

  const estado = (orden.nombreEstado || '').replace(/_/g, ' ').toUpperCase();

  let html =
    `✅ <strong>Orden localizada</strong><br><br>` +
    `📋 <strong>N° Orden:</strong> #${orden.numeroOrden || orden.idRecepcionEquipo}<br>` +
    `👤 <strong>Titular:</strong> ${orden.nombreCliente}<br>` +
    `💻 <strong>Equipo:</strong> ${orden.descripcion || 'Sin descripción'}<br>` +
    `⚠️ <strong>Falla reportada:</strong> ${orden.problema || 'N/A'}<br>` +
    `📅 <strong>Ingreso:</strong> ${fecha}<br>` +
    `⚡ <strong>Estado:</strong> ${estado}<br>`;

  if (totalUsd > 0) {
    html += `<br>💵 <strong>Total:</strong> $${totalUsd.toFixed(2)} USD (aprox. <strong>Bs. ${totalBs.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</strong>)`;
  }

  const opciones = [{ id: 'ord_estado_wa', label: '💬 Consultar por WhatsApp', accion: 'ABRIR_WHATSAPP_ORDEN' }];

  if (ordenPermitePago(orden)) {
    opciones.push({ id: 'ord_pago', label: '💳 Ir a pagar', accion: 'ABRIR_MODAL_PAGO', orden });
  } else {
    html += `<br><br>⏳ <em>Tu equipo aún está en proceso. El botón de pago se habilitará cuando esté </em><strong>reparado y listo para retirar</strong>.`;
  }

  opciones.push({ id: 'ord_menu', label: '🏠 Menú principal', accion: ESTADOS.MENU_PRINCIPAL });

  return { texto: html, opciones };
};

export const formatearCategorias = (productos = [], categoriasDisponibles = []) => {
  const mapCats = new Map();

  // 1. Agregar categorías disponibles desde la base de datos (si existen)
  if (Array.isArray(categoriasDisponibles)) {
    categoriasDisponibles.forEach((c) => {
      const nombre = (c.nombre || c.nombreCategoria || '').trim();
      if (nombre && normalizar(nombre) !== 'otros') {
        const key = normalizar(nombre);
        if (!mapCats.has(key)) {
          mapCats.set(key, { idCategoria: c.idCategoria, nombre });
        }
      }
    });
  }

  // 2. Agregar categorías encontradas en la lista de productos
  if (Array.isArray(productos)) {
    productos.forEach((p) => {
      const nombre = (p.categoria || p.nombreCategoria || p.categoria_nombre || '').trim();
      if (nombre && normalizar(nombre) !== 'otros') {
        const key = normalizar(nombre);
        if (!mapCats.has(key)) {
          mapCats.set(key, { idCategoria: p.idCategoria || null, nombre });
        }
      }
    });
  }

  const listaCats = Array.from(mapCats.values());

  return {
    texto: '💵 <strong>Catálogo de productos</strong><br><br>Elige una categoría o escríbeme el nombre del producto que buscas:',
    opciones: [
      ...listaCats.map((cat) => ({
        id: `cat_${cat.idCategoria || normalizar(cat.nombre)}`,
        label: `📦 ${cat.nombre}`,
        accion: 'FILTRAR_CATEGORIA',
        categoria: cat.nombre,
        idCategoria: cat.idCategoria
      })),
      { id: 'cat_todas', label: '🗂️ Ver todos los productos', accion: 'FILTRAR_CATEGORIA', categoria: null },
      { id: 'cat_volver', label: '⬅️ Volver al menú', accion: ESTADOS.MENU_PRINCIPAL }
    ]
  };
};

// ─────────────────────────────────────────────────────────────
// ⭐ FORMATEADOR DE PRODUCTOS (MUESTRA TODOS SIN LIMITAR A 8)
// ─────────────────────────────────────────────────────────────
export const formatearListaProductos = (productos = [], categoria = null, tasa = 1) => {
  const titulo = categoria ? `📦 <strong>${categoria}</strong>` : '🗂️ <strong>Todos los productos</strong>';
  
  // Mapeamos todos los productos (sin limitar a 8) para mostrar todo el catálogo
  const lista = (productos || []).map((p) => {
    // 1. Extraemos el precio en Dólares (soportando varias nomenclaturas)
    const precioUsd = parseFloat(
      p.precioVentaUsd || p.precio_venta_usd || p.precioVenta || p.precio_usd || p.precio || 0
    );
    
    // 2. Calculamos el precio en Bolívares usando la tasa
    const precioBs = precioUsd * (tasa || 1);
    
    // 3. Extraemos la imagen
    let imagenUrl = null;
    if (p.imagen && p.imagen !== 'no-image') {
      try {
        imagenUrl = typeof obtenerUrlImagen === 'function' ? obtenerUrlImagen(p.imagen) : p.imagen;
      } catch (e) {
        imagenUrl = p.imagen.startsWith('http') ? p.imagen : `http://localhost:8080/uploads/${p.imagen}`;
      }
    } else if (p.imagenUrl) {
      imagenUrl = p.imagenUrl;
    }

    return {
      id: p.idProducto || p.id,
      nombre: p.nombreProducto || p.nombre || 'Producto',
      categoria: p.categoria || p.nombreCategoria || 'Tecnología',
      imagen: imagenUrl,
      precioUsdFormatted: formatUSD(precioUsd),
      precioBsFormatted: formatBs(precioBs)
    };
  });

  if (lista.length === 0) {
    return {
      texto: `${titulo}<br><br>No hay productos disponibles en esta categoría ahora mismo.`,
      opciones: [
        { id: 'prod_volver', label: '⬅️ Ver categorías', accion: ESTADOS.PRECIOS_MENU_CATEGORIAS },
        { id: 'prod_menu', label: '🏠 Menú principal', accion: ESTADOS.MENU_PRINCIPAL }
      ]
    };
  }

  const cantidadTexto = lista.length === 1 ? '1 producto encontrado' : `${lista.length} productos disponibles`;

  return {
    texto: `${titulo} <small style="opacity: 0.85;">(${cantidadTexto})</small><br><br>Aquí tienes los productos disponibles:`,
    productos: lista,
    opciones: [
      { id: 'prod_volver', label: '⬅️ Ver categorías', accion: ESTADOS.PRECIOS_MENU_CATEGORIAS },
      { id: 'prod_menu', label: '🏠 Menú principal', accion: ESTADOS.MENU_PRINCIPAL }
    ]
  };
};
